export default defineNuxtRouteMiddleware((to) => {
  const { $i18n, $env } = useNuxtApp();
  const { path, query, hash } = to;
  const config = useRuntimeConfig();

  // Игнорируем технич. пути
  if (
    path.startsWith('/admin/') ||
    path.startsWith('/data/') ||
    path.startsWith('/api/') ||
    path.startsWith('/_nuxt/') ||
    path.startsWith('/nuxt/') ||
    path.startsWith('/assets/')
  ) return;

  // Доступные коды локалей (['ru','en','ka', ...])
  const locales = $i18n.locales.value.map(l => (typeof l === 'string' ? l : l.code));
  const cookieDomain = config.public.cookieDomain;

  // Кука локали
  const localeCookie = useCookie<string | undefined>('donut-locale', {
    maxAge: 31536000,
    domain: cookieDomain,
    sameSite: 'lax',
    secure: $env.isProd,
    path: '/',
  });

  // Разбор пути
  const segments = path.split('/').filter(Boolean); // ['ru', 'products', ...]
  const first = segments[0]; // может быть код локали
  const hasPrefix = !!first && locales.includes(first);
  const currentPathLocale = hasPrefix ? first : undefined;

  // Если второй сегмент - admin, перенаправляем на baseUrl/admin без префикса локали
  if (segments[1] === 'admin') {
    // Формируем URL без префикса локали
    const adminPath = `/admin${segments.slice(2).length > 0 ? '/' + segments.slice(2).join('/') : ''}`;

    let targetUrl;

    // Если передан GET-параметр project, формируем URL с project + baseUrl + admin
    if (query.project) {
      // Разбиваем baseUrl на протокол и хост
      const [protocol, host] = config.public.baseUrl.split('://');

      // Формируем URL в виде project + baseUrl + admin
      targetUrl = `${protocol}://${query.project}.${host}${adminPath}`;

      // Удаляем параметр project из query, так как он уже использован в URL
      const newQuery = { ...query };
      delete newQuery.project;

      return navigateTo({ path: targetUrl, query: newQuery, hash }, { redirectCode: 301, external: true });
    } else {
      return;
    }
  }

  // Определяем желаемую локаль:
  // приоритет: префикс в URL → кука → язык браузера → 'ru'
  const browserLang = process.client ? (navigator.language || 'ru').split('-')[0] : 'ru';
  const desiredLocale =
    currentPathLocale ||
    (localeCookie.value && locales.includes(localeCookie.value) ? localeCookie.value : undefined) ||
    (locales.includes(browserLang) ? browserLang : 'ru');

  // Синхронизируем i18n и куку
  if ($i18n.locale.value !== desiredLocale) $i18n.setLocale(desiredLocale);
  if (localeCookie.value !== desiredLocale) localeCookie.value = desiredLocale;

  // --- САНИТАЦИЯ ПУТИ ---
  // 1) Если встретился двойной префикс локали (например /ru/ka/...), оставляем только второй (целевой)
  if (hasPrefix && segments.length > 1 && locales.includes(segments[1])) {
    const fixed = '/' + [segments[1], ...segments.slice(2)].join('/');
    if (fixed !== path) {
      return navigateTo({ path: fixed, query, hash }, { redirectCode: 301 });
    }
  }

  // 2) Если префикса нет — добавляем один корректный
  if (!hasPrefix) {
    const fixed = '/' + [desiredLocale, ...segments].join('/');
    if (fixed !== path) {
      return navigateTo({ path: fixed, query, hash }, { redirectCode: 301 });
    }
  }

  // 3) Если префикс есть, но он НЕ тот, что нужен — заменяем на нужный
  if (hasPrefix && currentPathLocale !== desiredLocale) {
    const fixed = '/' + [desiredLocale, ...segments.slice(1)].join('/');
    if (fixed !== path) {
      return navigateTo({ path: fixed, query, hash }, { redirectCode: 301 });
    }
  }

  // Иначе ничего не делаем
});
